import { useMemo } from "react";
import { useLoaderData } from "react-router";
import ContentComponents from "~/components/content/ContentComponents";
import { FormFlowContext } from "~/components/hooks/formFlowContext";
import { useFocusFirstH1 } from "~/components/hooks/useFocusFirstH1";
import type { loader as loaderFormular } from "../formular";
import { GridSection } from "~/components/layout/grid/GridSection";
import { Grid } from "~/components/layout/grid/Grid";
import { GridItem } from "~/components/layout/grid/GridItem";
import { MissingDataList } from "~/components/common/MissingDataList";
import ArraySummary from "~/components/content/arraySummary/ArraySummary";
import ValidatedFlowForm from "~/components/formElements/ValidatedFormFlow";
import FlowNavigation from "~/components/navigation/FlowNavigation";
import AutoGeneratedSummary from "~/components/content/autoGeneratedSummary/AutoGeneratedSummary";
import SideNavMobile from "~/components/navigation/SideNavMobile";
import { NavigationList } from "~/components/navigation/NavigationList";
import { FlowStepperNavigation } from "~/components/navigation/FlowStepperNavigation/FlowStepperNavigation";
import Heading from "~/components/common/Heading";
import MigrationDataOverview from "~/components/content/MigrationDataOverview";
import { ReportProblem } from "~/components/content/reportProblem/ReportProblem";
import { useFlowExtras } from "~/domains/extraLoaderConfiguration";

export function FormFlowPage() {
  const loaderData = useLoaderData<typeof loaderFormular>();
  const {
    arraySummaryData,
    userData,
    buttonNavigationProps,
    cmsContent,
    formElements,
    migration,
    navigationProps,
    stepData,
    translations,
    validFlowPaths,
    flowId,
    showReportProblem,
    autoGeneratedSections,
  } = loaderData;

  const formFlowMemo = useMemo(
    () => ({
      userData,
      validFlowPages: validFlowPaths,
      translations: translations,
      flowId,
    }),
    [userData, validFlowPaths, translations, flowId],
  );

  const { extraComponents: additionalComponents, dynamicOptions } =
    useFlowExtras(loaderData.extraData ?? {}, flowId) ?? {};

  useFocusFirstH1();

  return (
    <FormFlowContext.Provider value={formFlowMemo}>
      <GridSection pt="40" pb="40" className="bg-kern-neutral-025">
        <Grid>
          <GridItem
            mdColumn={{ start: 1, span: 12 }}
            lgColumn={{ start: 1, span: 12 }}
            xlColumn={{ start: 1, span: 12 }}
            row={1}
            className="hidden lg:block pb-kern-space-default"
          >
            <FlowStepperNavigation steps={navigationProps.stepsStepper} />
          </GridItem>
          <GridItem
            className="hidden lg:block"
            lgColumn={{ start: 1, span: 4 }}
            xlColumn={{ start: 1, span: 4 }}
            row={2}
          >
            <div className="md:mb-32 lg:w-[312px]">
              <FlowNavigation>
                <NavigationList {...navigationProps} />
              </FlowNavigation>
            </div>
          </GridItem>
          <GridItem
            mdColumn={{ start: 1, span: 8 }}
            lgColumn={{ start: 5, span: 7 }}
            xlColumn={{ start: 5, span: 7 }}
            row={3}
            as="section"
          >
            <div
              className="flex flex-col flex-1 justify-between gap-kern-space-default"
              id="flow-page-content"
            >
              {cmsContent.preHeading && (
                <p className="text-kern-18 kern-label text-kern-layout-text-muted! pb-2 pt-6">
                  {cmsContent.preHeading}
                </p>
              )}
              <Heading
                text={cmsContent.heading}
                className="kern-heading-large pb-7! pt-9!"
                managedByParent
              />
              <div className="flex flex-col gap-kern-space-x-large">
                <ContentComponents
                  content={cmsContent.content}
                  managedByParent
                />
                {additionalComponents}
                <MissingDataList
                  // Renders a list of menu entries with missing data on pages that have triggerValidation / expandAll set to true
                  // This is a temporary workaround until this functionality is implemented into the summary page
                  navItems={navigationProps.navItems}
                  shouldRender={navigationProps.expandAll}
                />

                {/* Auto-generated summary sections */}
                {autoGeneratedSections && autoGeneratedSections.length > 0 && (
                  <AutoGeneratedSummary items={autoGeneratedSections} />
                )}

                <MigrationDataOverview
                  userData={migration.userData}
                  translations={translations}
                  sortedFields={migration.sortedFields}
                  buttonUrl={migration.buttonUrl}
                />
                {arraySummaryData &&
                  Object.keys(arraySummaryData).length !== 0 &&
                  Object.entries(arraySummaryData).map(([category, array]) => (
                    <ArraySummary
                      key={category}
                      category={category}
                      arrayData={{
                        configuration: array.configuration,
                        data: array.data,
                      }}
                      content={{
                        buttonLabel: array.buttonLabel,
                        description: array.description,
                        subtitle: array.subtitle,
                        title: array.title,
                        itemLabels: array.itemLabels,
                      }}
                    />
                  ))}
                <ValidatedFlowForm
                  stepData={stepData}
                  formElements={formElements}
                  buttonNavigationProps={buttonNavigationProps}
                  dynamicOptions={dynamicOptions}
                />
                <ContentComponents
                  content={cmsContent.postFormContent}
                  managedByParent
                />
              </div>
            </div>
          </GridItem>
          {showReportProblem && (
            <GridItem
              mdColumn={{ start: 1, span: 8 }}
              lgColumn={{ start: 7, span: 5 }}
              xlColumn={{ start: 7, span: 5 }}
              className="pb-kern-space-x-large pt-kern-space-x-large flex justify-end"
              row={4}
            >
              <ReportProblem />
            </GridItem>
          )}
          <div className="lg:hidden md:col-span-8 ">
            <FlowNavigation>
              <SideNavMobile
                navItems={navigationProps.navItems}
                stepsStepper={navigationProps.stepsStepper}
              />
            </FlowNavigation>
          </div>
        </Grid>
      </GridSection>
    </FormFlowContext.Provider>
  );
}
